
import { ViewportStoreModule } from "@stores/viewport.store";
import { computed, defineComponent, getCurrentInstance, onMounted, PropType, Ref, ref, watch } from "vue";
import { getModule } from "vuex-module-decorators";
import { CurrentUser } from "@utils/user";
import { chartService } from "@services/charts.service";
import PerformanceChartRequest from "@services/performance_charts/performance-chart-request";
import { useRouter } from "vue-router/composables";
import { ROUTENAMES } from "@models/constants/routes";
import { useI18n } from "@composables/i18n";
import { Period } from "@models/enums";
import { CHART_PERIODS } from "@services/charts.helper";

const ViewportStore = getModule(ViewportStoreModule);

export default defineComponent({
  name: "CiPortfolioComparisonTile",
  props: {
    portfolio: {
      type: Object as PropType<Portfolios.TileData>,
      required: true,
    },

    performanceInterval: {
      type: Object as PropType<Portfolios.PerformanceInterval>,
      default: () => ({
        period: 1,
        category: "year",
        performance_key: Period.YEAR_1,
        availableIndexes: { perf: "_perf_1y_", vola: "_vola_1y_" },
      }),
    },
  },

  setup(props) {
    const portfolio = ref(props.portfolio);
    const { t } = useI18n(getCurrentInstance());

    const kpis = computed(() => {
      const kpis: Portfolios.Kpi[] = [{ name: "performance" }, { name: "volatility" }];
      const periodCategory = props.performanceInterval.category;

      kpis.map((kpi) => {
        const metric = `${kpi.name}${props.performanceInterval.performance_key}` as keyof Portfolios.SortingMetrics;

        kpi["value"] = (portfolio.value.performance as Record<string, Nullable<number>>)?.[metric];
        kpi["label"] = `ui.shared.global.metrics.${kpi.name}.abbr_with_${periodCategory}_period`;
      });

      return kpis;
    });

    const mobileMode = computed(() => ViewportStore.smartphone);

    const isOwner = computed((): boolean =>
      portfolio.value.owner.type === "User" ? CurrentUser.id === portfolio.value.owner.id : false
    );
    const ownerLink = computed((): string => {
      if (isOwner.value) return portfolio.value.owner.url;

      return "";
    });

    const chartData: Ref<Nullable<Highcharts.Options>> = ref(null);
    const dataDisabled = computed(
      (): boolean => portfolio.value.portfolio_products.map((pp) => pp.product).length === 0 || dataIrregularity.value
    );
    const dataIrregularity = computed((): boolean => {
      const funds = portfolio.value.portfolio_products;

      return (
        funds.map((pp) => pp.product).some((product) => !product.isin) ||
        (chartData.value && funds.length > 0 ? (chartData.value!.series![0] as any).data!.length === 0 : false)
      );
    });
    const disableChartText = computed((): string =>
      portfolio.value.portfolio_products.length > 0
        ? (t("ui.shared.portfolios.insufficient_data") as string)
        : (t("ui.shared.portfolios.no_products_available") as string)
    );

    const portfolioChartSeries = computed(() => {
      const performanceRequest = new PerformanceChartRequest({
        euro: true,
      });

      const portfolioRequest = performanceRequest.addPortfolio(portfolio.value);
      return portfolioRequest.chart();
    });

    const getChartData = async (): Promise<void> => {
      if (dataDisabled.value) {
        chartData.value = chartService.portfolioTileChart(
          [
            {
              fillColor: "transparent",
              color: chartService.colors.primary,
              colorIndex: 0,
              data: [],
              name: "",
            } as any,
          ],
          {
            minMax: 30,
          }
        );
        return;
      }

      chartData.value = {
        ...chartService.portfolioTileChart(
          [
            {
              fillColor: "transparent",
              color: chartService.colors.primary,
              colorIndex: 0,
              data: await portfolioChartSeries.value,
              name: "",
            } as any,
          ],
          {
            selectedPeriod: CHART_PERIODS().findIndex((period) =>
              props.performanceInterval.performance_key.match(period.id)
            ),
            height: ["xl", "xxl"].includes(ViewportStore.currentViewport) ? "150px" : "200px",
          }
        ),
      };
    };

    const router = useRouter();
    const redirectToPorfolio = () => {
      router.push({ name: ROUTENAMES.PORTFOLIOS_SHOW, params: { id: portfolio.value.id.toString() } });
    };
    const handleOwnerClicked = (event: Event) => event.stopPropagation();

    onMounted(() => {
      getChartData();
    });

    watch(() => props.performanceInterval.performance_key, getChartData, { deep: true });

    return {
      kpis,
      mobileMode,
      isOwner,
      ownerLink,
      chartData,
      dataDisabled,
      disableChartText,
      redirectToPorfolio,
      handleOwnerClicked,
    };
  },
});
